import { createSignal, For, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { Portal } from "solid-js/web";
import { TransitionGroup } from "@fleet/ui";
import { AlertCircle, CheckCircle2, Info, X } from "@fleet/icons";

import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { TODO_ROW_SWIPE } from "@/lib/gestures/presets";

type ToastType = "success" | "error" | "info" | "warning";

/** Inline action of the toast (e.g. "Undo"). */
interface ToastAction {
  label: string;
  onAction: () => void;
}

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  action?: ToastAction;
}

const TOAST_DURATION = 5000;
const RADIX_36 = 36;
const ID_LENGTH = 9;
const [toasts, setToasts] = createStore<Toast[]>([]);

export function showToast(message: string, type: ToastType = "info") {
  const id = Math.random().toString(RADIX_36).slice(2, ID_LENGTH);
  setToasts([...toasts, { id, message, type }]);

  setTimeout(() => {
    setToasts(toasts.filter((t) => t.id !== id));
  }, TOAST_DURATION);
}

const FLASH_ID = "__saved_flash__";
const FLASH_DURATION = 1400;
let flashTimer: ReturnType<typeof setTimeout> | undefined;

const UNDO_FLASH_ID = "__undo_flash__";
const UNDO_FLASH_DURATION = 6000;
let undoFlashTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Coalesced undoable toast (L1): a single "action done · Undo" pill
 * that refreshes on each mutation instead of stacking notifications (checking 5
 * habits in a row = 1 live pill, not 5 toasts). Auto-dismiss after ~6s; the
 * complete history remains on the undo stack (Ctrl+Z steps back further than
 * the visible pill).
 */
export function showUndoFlash(message: string, action: ToastAction) {
  if (toasts.some((t) => t.id === UNDO_FLASH_ID)) {
    // Already visible: update in place (without re-animating) and refresh timer.
    setToasts((t) => t.id === UNDO_FLASH_ID, { message, action });
  } else {
    setToasts([...toasts, { id: UNDO_FLASH_ID, message, type: "success", action }]);
  }
  if (undoFlashTimer) {
    clearTimeout(undoFlashTimer);
  }
  undoFlashTimer = setTimeout(() => {
    dismissUndoFlash();
  }, UNDO_FLASH_DURATION);
}

/** Dismisses undo pill (after undoing, or when switching accounts). */
export function dismissUndoFlash() {
  if (undoFlashTimer) {
    clearTimeout(undoFlashTimer);
    undoFlashTimer = undefined;
  }
  setToasts(toasts.filter((t) => t.id !== UNDO_FLASH_ID));
}

/**
 * Coalesced "saved" confirmation. Marking several habits quickly should NOT
 * stack toasts — this keeps a single short-lived pill that refreshes its timer
 * on each call, giving calm "it's saved" feedback without notification spam.
 */
export function showSavedFlash(message: string) {
  if (toasts.some((t) => t.id === FLASH_ID)) {
    setToasts((t) => t.id === FLASH_ID, "message", message);
  } else {
    setToasts([...toasts, { id: FLASH_ID, message, type: "success" }]);
  }
  if (flashTimer) {
    clearTimeout(flashTimer);
  }
  flashTimer = setTimeout(() => {
    setToasts(toasts.filter((t) => t.id !== FLASH_ID));
  }, FLASH_DURATION);
}

function removeToast(id: string) {
  setToasts(toasts.filter((t) => t.id !== id));
}

// oxlint-disable-next-line lingui/no-unlocalized-strings -- CSS media query, not UI copy
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

const {
  thresholdPx: SWIPE_THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  rubberBandLimitPx: RUBBER_BAND_LIMIT,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  hapticMs: HAPTIC_VIBRATION_MS,
  commitAnimationMs: COMMIT_ANIMATION_MS,
  cancelAnimationMs: CANCEL_ANIMATION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
} = TODO_ROW_SWIPE;

const DISMISS_VELOCITY_OFFSET_PX = 24;

/**
 * Swipe-to-dismiss for a single toast, on the same standard as the habit/todo
 * row swipe: the shared recognizer owns the deadzone, axis-lock, rubber-band and
 * the rising-edge commit haptic. Either direction (left or right) past the
 * commit threshold — or a fling — flicks the toast off the locked axis and then
 * removes it; anything short bounces back. Honors reduced-motion (no transition,
 * instant snap) and keeps auto-dismiss + the close button intact.
 */
function useToastSwipe(getToastId: () => string, getSurfaceRef: () => HTMLElement | undefined) {
  const [tx, setTx] = createSignal(0);
  const [opacity, setOpacity] = createSignal(1);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [isAnimating, setIsAnimating] = createSignal(false);

  const fadeFor = (offset: number) => {
    const ratio = Math.min(Math.abs(offset) / (RUBBER_BAND_LIMIT || 1), 1);
    return 1 - ratio * 0.6;
  };

  const flickOut = (direction: 1 | -1) => {
    const width = getSurfaceRef()?.clientWidth ?? FALLBACK_WIDTH_PX;
    if (prefersReducedMotion()) {
      removeToast(getToastId());
      return;
    }
    setIsAnimating(true);
    setTx(direction * (width + DISMISS_VELOCITY_OFFSET_PX));
    setOpacity(0);
    setTimeout(() => {
      removeToast(getToastId());
    }, COMMIT_ANIMATION_MS);
  };

  const snapBack = () => {
    setIsAnimating(true);
    setTx(0);
    setOpacity(1);
    setTimeout(() => {
      setIsAnimating(false);
    }, CANCEL_ANIMATION_MS);
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: SWIPE_THRESHOLD_PX,
    commitPx: COMMIT_THRESHOLD_PX,
    haptics: true,
    hapticMs: HAPTIC_VIBRATION_MS,
    fling: { maxDurationMs: 300, minDistancePx: 50, axisRatio: 2 },
    onStart: () => {
      setIsSwiping(true);
      setIsAnimating(false);
    },
    onMove: (sample) => {
      const offset = applyRubberBand(sample.dx, RUBBER_BAND_LIMIT, RUBBER_BAND_FACTOR);
      setTx(offset);
      setOpacity(fadeFor(offset));
    },
    onEnd: (sample) => {
      setIsSwiping(false);
      const dismissed = sample.isFling || sample.distance > COMMIT_THRESHOLD_PX;
      if (dismissed) {
        flickOut(sample.dx >= 0 ? 1 : -1);
      } else {
        snapBack();
      }
    },
    onCancel: () => {
      setIsSwiping(false);
      snapBack();
    },
  });

  const handlers = pointerHandlers(recognizer);

  return { tx, opacity, isSwiping, isAnimating, handlers };
}

function ToastItem(props: { toast: Toast }) {
  let surfaceRef: HTMLDivElement | undefined;
  const swipe = useToastSwipe(
    () => props.toast.id,
    () => surfaceRef,
  );

  return (
    <div
      ref={surfaceRef}
      // Error toasts must interrupt assistive tech immediately (CC-30): an API
      // failure surfaced here is the only feedback the user gets, so it uses the
      // assertive `alert` live region. Non-errors stay polite `status`.
      role={props.toast.type === "error" ? "alert" : "status"}
      aria-live={props.toast.type === "error" ? "assertive" : "polite"}
      class="flex items-center justify-between gap-3 p-4 rounded-lg border shadow-lg bg-popover select-none [touch-action:pan-y]"
      classList={{
        "border-success/30 text-success": props.toast.type === "success",
        "border-destructive/30 text-destructive": props.toast.type === "error",
        "border-primary/30 text-primary": props.toast.type === "info",
        "transition-transform duration-200 ease-out": swipe.isAnimating(),
      }}
      style={{
        transform: `translateX(${swipe.tx()}px)`,
        opacity: swipe.opacity(),
        cursor: swipe.isSwiping() ? "grabbing" : undefined,
      }}
      onPointerDown={swipe.handlers.onPointerDown}
      onPointerMove={swipe.handlers.onPointerMove}
      onPointerUp={swipe.handlers.onPointerUp}
      onPointerCancel={swipe.handlers.onPointerCancel}
    >
      <div class="flex items-center gap-3 min-w-0">
        <Show when={props.toast.type === "success"}>
          <CheckCircle2 class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "error"}>
          <AlertCircle class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "info"}>
          <Info class="h-5 w-5 shrink-0" />
        </Show>
        <span class="text-sm font-medium break-words">{props.toast.message}</span>
      </div>
      <div class="flex items-center gap-1 shrink-0">
        <Show when={props.toast.action}>
          {(action) => (
            <button
              type="button"
              data-testid="toast-action"
              onClick={() => {
                removeToast(props.toast.id);
                action().onAction();
              }}
              class="rounded-md px-2 py-1 text-sm font-semibold underline-offset-2 hover:underline hover:bg-black/5 transition-colors"
            >
              {action().label}
            </button>
          )}
        </Show>
        <button
          type="button"
          onClick={() => {
            removeToast(props.toast.id);
          }}
          class="p-1 hover:bg-black/5 rounded-full transition-colors shrink-0"
        >
          <X class="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function Toaster() {
  return (
    <Portal>
      {/*
       * Mobile: span the viewport between the side safe-area insets (no
       * horizontal overflow, never under the notch). Desktop (sm+): release the
       * left edge and pin a capped-width stack to the right.
       */}
      <div
        class="fixed z-[300] flex flex-col gap-2 pointer-events-none sm:left-auto sm:w-[420px] sm:max-w-[calc(100vw-2rem)]"
        style={{
          bottom: "calc(1rem + var(--safe-area-inset-bottom))",
          left: "calc(1rem + var(--safe-area-inset-left))",
          right: "calc(1rem + var(--safe-area-inset-right))",
        }}
      >
        <TransitionGroup name="toast">
          <For each={toasts}>
            {(toast) => (
              <div class="pointer-events-auto">
                <ToastItem toast={toast} />
              </div>
            )}
          </For>
        </TransitionGroup>
      </div>
    </Portal>
  );
}
